import React, {
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Prompt,
  useNavigate,
  useParams,
} from 'react-router-dom';
import {
  useDispatch,
  useSelector
} from 'react-redux';
import {
  Form,
  FormSpy,
} from 'react-final-form';
import clsx from 'clsx';
import { useSnackbar } from 'notistack';
import slugify from 'slugify';
import { makeValidate } from 'mui-rff';
import * as Yup from 'yup';
import {
  Container,
  CircularProgress,
  Fab,
  Grid,
  Tooltip,
  makeStyles
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SaveIcon from '@material-ui/icons/Save';

import {
  createCategory,
  deleteCategory,
  fetchCategories,
  fetchCategoryDetails,
  updateCategory,
} from 'src/store/actions/categories';
import categoriesSelectors from 'src/store/selectors/categories';

import AlertDialog from 'src/components/AlertDialog';
import Loading from 'src/components/Loading';
import Page from 'src/components/Page';

import ImageBox from './ImageBox';
import CategoryDetails from './CategoryDetails';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(12),
    paddingTop: theme.spacing(3)
  },
  deleteButton: {
    background: theme.palette.error.main,
    color: '#fff',
    '&:hover': {
      background: theme.palette.error.dark,
    }
  },
  floatingActionButton: {
    position: 'absolute',
    bottom: theme.spacing(3),
    right: theme.spacing(4),
  },
}));

// Validation
const schema = Yup.object().shape({
  name: Yup.string()
    .required('Pole jest wymagane'),
  type: Yup.string()
    .required('Pole jest wymagane'),
  tags: Yup.string()
    .required('Pole jest wymagane'),
  status: Yup.string()
    .required('Pole jest wymagane'),
});

const CategoryDetailView = ({ action }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams();
  const classes = useStyles();
  const deleteAlertRef = useRef();
  const exitAlertRef = useRef();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0); // eslint-disable-line

  const category = useSelector((state) => categoriesSelectors.getCategoryDetails(state));
  const categories = useSelector((state) => categoriesSelectors.getCategories(state));
  const isFetching = useSelector((state) => categoriesSelectors
    .getCategoryDetailsIsFetching(state));

  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (action === 'edit') {
      const fetchData = async () => {
        await dispatch(fetchCategoryDetails(id));

        if (categories.length === 0) {
          await dispatch(fetchCategories());
        }
      };

      fetchData()
        .catch(() => {
          enqueueSnackbar('Wystąpił problem podczas pobierania danych', { variant: 'error' });
        });
    } else {
      // console.log(validate);
    }
  }, [action, categories, dispatch, enqueueSnackbar, id]);

  const openExitConfirmModal = () => {
    exitAlertRef.current.open();
    setTimeout(() => { // To disable prompt
      forceUpdate();
    });
  };

  const createSlug = (title) => {
    return slugify(title, {
      lower: true,
      remove: /[*+~.()'"!:@]/g,
    });
  };

  const getSubmitIcon = (status) => {
    if (action === 'add') {
      return <AddIcon />;
    }

    if (status === 'deleted') {
      return <DeleteIcon />;
    }

    return <SaveIcon />;
  };

  const getSubmitLabel = (status) => {
    if (action === 'add') {
      return 'Dodaj';
    }

    if (status === 'deleted') {
      return 'Usuń';
    }

    return 'Zapisz zmiany';
  };

  const onSubmit = async (values) => {
    if (values.status === 'deleted') {
      deleteAlertRef.current.open();
      return false;
    }

    const categoryData = new FormData();

    Object.entries(values).forEach((entry) => {
      const [key, value] = entry;

      if (key === 'tags') {
        categoryData.append(key, value.map((v) => v.value || v.id));
      } else {
        categoryData.append(key, value);
      }
    });

    if (imageFile != null) {
      categoryData.append('image', imageFile ? imageFile[0].file : false);
    }

    try {
      if (action === 'edit') {
        await dispatch(updateCategory(id, categoryData));
        enqueueSnackbar('Zmiany zostały zapisane', { variant: 'success' });
      } else {
        await dispatch(createCategory(categoryData));
        enqueueSnackbar('Dodano nową kategorię', { variant: 'success' });

        setTimeout(() => {
          navigate('/categories');
        });
      }

      return true;
    } catch (error) {
      enqueueSnackbar(error.message || 'Wystąpił błąd podczas wysyłania danych', { variant: 'error' });

      return error;
    }
  };

  const onDelete = async () => {
    try {
      await dispatch(deleteCategory(id));
      enqueueSnackbar('Kategoria została usunięta', { variant: 'success' });

      setTimeout(() => {
        navigate('/categories');
      });

      return true;
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Wystąpił błąd podczas wysyłania danych', { variant: 'error' });

      return error;
    }
  };

  return (
    <Form
      initialValues={
        category ? {
          name: category.name,
          slug: category.slug,
          image: category.image,
          description: category.description,
          parentId: category.parentId,
          type: category.type,
          tags: category.tags.map((value) => ({ name: value.name, value: value.id })),
          status: category.status,
        } : {
          status: 'visible',
        }
      }
      mutators={{
        setSlug: (args, state, utils) => {
          const slug = createSlug(args[0]);
          if (action === 'add' && !state.fields.slug.modified) {
            utils.changeValue(state, 'slug', () => slug);
          }
        },
      }}
      onSubmit={onSubmit}
      subscription={{
        dirty: true,
        submitSucceeded: true,
      }}
      validate={makeValidate(schema)}
      render={({
        dirty,
        form,
        handleSubmit,
        submitSucceeded,
      }) => (
        <Page
          appBar={{
            title: action === 'edit' ? 'Edycja kategorii' : 'Dodaj nową kategorię',
            hasBackButton: true,
            backTo: '/categories',
            onBack: () => {
              if (dirty && !submitSucceeded) {
                openExitConfirmModal();
                return true;
              }
              return false;
            }
          }}
          className={classes.root}
          title={
            action === 'edit'
              ? `Edycja kategorii${category ? `: ${category.name} [ID: #${category.id}]` : ''}`
              : 'Dodaj nową kategorię'
          }
        >
          {(action === 'add' || category) ? (
            <form onSubmit={handleSubmit} noValidate>
              <Container maxWidth="lg">
                <Grid
                  container
                  spacing={3}
                >
                  <Grid
                    item
                    lg={4}
                    md={6}
                    xs={12}
                  >
                    <ImageBox
                      data={category}
                      file={imageFile}
                      setFile={setImageFile}
                    />
                  </Grid>
                  <Grid
                    item
                    lg={8}
                    md={6}
                    xs={12}
                  >
                    <CategoryDetails
                      data={category}
                      setSlug={form.mutators.setSlug}
                    />
                  </Grid>
                </Grid>
                <FormSpy
                  subscription={{
                    submitting: true,
                    values: true,
                  }}
                >
                  {({ submitting, values }) => (
                    <Tooltip
                      placement="left"
                      title={getSubmitLabel(values.status)}
                    >
                      <Fab
                        aria-label="save"
                        className={clsx(
                          classes.floatingActionButton,
                          values.status === 'deleted' && classes.deleteButton,
                        )}
                        color="secondary"
                        disabled={submitting}
                        type="submit"
                      >
                        {!submitting
                          ? getSubmitIcon(values.status)
                          : <CircularProgress color="secondary" size={26} />}
                      </Fab>
                    </Tooltip>
                  )}
                </FormSpy>
              </Container>
              <Prompt
                when={dirty && !submitSucceeded && !exitAlertRef.current?.isOpen}
                message="Czy pewno chcesz opuścić stronę bez zapisywania zmian?"
              />
              <AlertDialog
                title="Nie zapisano zmian"
                message="Czy pewno chcesz opuścić stronę bez zapisywania zmian?"
                ref={exitAlertRef}
                actions={[
                  {
                    label: 'Wyjdź bez zapisywania',
                    onClick: () => {
                      navigate('/categories');
                    },
                  },
                  {
                    autoFocus: true,
                    label: 'Wróć',
                    variant: 'contained',
                  },
                ]}
              />
              <AlertDialog
                title="Czy na pewno chcesz usunąć tą kategorię?"
                ref={deleteAlertRef}
                actions={[
                  {
                    label: 'Wróć',
                  },
                  {
                    className: classes.deleteButton,
                    disabled: isFetching,
                    label: 'Usuń',
                    loading: isFetching,
                    onClick: () => {
                      onDelete();
                      deleteAlertRef.current.close();
                    },
                  },
                ]}
              />
            </form>
          ) : (
            <Loading />
          )}
        </Page>
      )}
    />
  );
};

CategoryDetailView.propTypes = {
  action: PropTypes.string,
};

CategoryDetailView.defaultProps = {
  action: 'add',
};

export default CategoryDetailView;
